import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { paragraphLarge } from '@helpers/typography';
import ArticleCardStyles from '@stories/Components/Cards/ArticleCard/ArticleCard.styles';
import ImageCtaCardStyles from '@stories/Components/Cards/ImageCtaCard/ImageCtaCard.styles';
import ProductCardStyles from '@stories/Components/Cards/ProductCard/ProductCard.styles';
import TechnicalDocumentCardStyles from '@stories/Components/Cards/TechnicalDocumentCard/TechnicalDocumentCard.styles';
import ButtonStyles from '@stories/Components/Global/Buttons/Button/Button.styles';
import TabButtonStyles from '@stories/Components/Global/Buttons/TabButton/TabButton.styles';
import { m } from 'framer-motion';
import styled from 'styled-components';
const Container = styled(m.div) `
  ${scrollbars};

  position: absolute;
  top: var(--headerHeight);
  left: 0;
  width: 100%;
  background-color: var(--backgroundColour);
  color: var(--textColour);
  max-height: calc(100vh - var(--headerHeight));
  max-height: calc(100dvh - var(--headerHeight));
  overflow: auto;
  overscroll-behavior: contain;
  z-index: 9;

  &::-webkit-scrollbar-thumb {
    [data-site='mep'] & {
      --fgColor: ${brand.global.red};
    }
  }

  [data-scroll-active='true'] & {
    max-height: calc(100vh - var(--stickyHeaderHeight) - var(--navigationHeight) - 16px);
  }

  @media ${until(Device.TabletLarge)} {
    [data-has-phone='true'] & {
      top: calc(var(--headerHeight) + var(--mobilePhoneNumberHeight));
      max-height: calc(100vh - var(--headerHeight) - var(--mobilePhoneNumberHeight));
      max-height: calc(100dvh - var(--headerHeight) - var(--mobilePhoneNumberHeight));
    }
  }

  [data-site='ess'] & {
    --backgroundColour: ${brand.ess.primary};
    --textColour: ${brand.global.white};
  }

  [data-site='mep'] & {
    --backgroundColour: ${brand.global.white};
    --textColour: ${brand.mep.primary};

    @media ${from(Device.TabletLarge)} {
      top: calc(var(--headerHeight) - var(--navigationHeight) - 3px);
    }
  }
`;
const Inner = styled.div `
  width: calc(100% - ${fluid(44, 128)});
  margin: ${fluid(26, 44)} auto ${fluid(34, 82)};
  position: relative;
  height: 100%;

  @media ${from(Device.TabletLarge)} {
    ${baseGrid};

    padding: 0 ${fluid(42, 64)};
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};
  ${transition('opacity, transform')};

  position: absolute;
  top: 0;
  right: 0;
  color: var(--textColour);
  width: 21px;
  height: 21px;
  transform: translateY(-50%);

  > svg {
    display: block;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 5px;
    outline-width: 2px;
    outline-style: solid;
  }

  &.focus-ring,
  &:hover {
    opacity: 0.7;
    transform: scale(1.1) translateY(-50%);
  }
`;
const TabsWrapper = styled.div `
  display: flex;
  gap: ${fluid(16, 24)};
  margin: 0 0 32px;

  @media ${until(Device.TabletLarge)} {
    overflow: auto;

    ${TabButtonStyles.Text} {
      white-space: nowrap;
    }
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: span 7;
  }

  @media ${from(Device.Desktop)} {
    grid-column: span 8;
  }

  ${TabButtonStyles.Container} {
    --colourBgActive: ${brand.global.red};
  }

  ${TabButtonStyles.Container}[data-active='true'] {
    color: var(--colourTextActive);
    border-color: var(--colourBorderActive);
  }

  [data-site='ess'] & {
    ${TabButtonStyles.Container} {
      --colourBg: ${brand.ess.secondary.darkestGrey};
      --colourBorder: ${brand.ess.secondary.darkestGrey};
      --colourBorderActive: ${brand.global.red};
      --colourText: ${brand.global.white};
      --colourBorderActive: ${brand.global.red};
    }
  }
`;
const CtaWrapper = styled.div `
  display: flex;

  ${ButtonStyles.Container} {
    margin: 24px auto 0;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 11 / span 2;
    grid-row: 1;

    ${ButtonStyles.Container} {
      margin-right: 0;
      margin-top: 0;
    }
  }

  [data-site='mep'] & {
    ${ButtonStyles.Container} {
      --colourBorder: var(--textColour);
      --colourBorderActive: var(--textColour);
      --colourText: var(--textColour);
      --colourTextActive: var(--textColour);
      --colourBg: transparent;
      --colourBgActive: ${brand.mep.secondary.midGrey};
    }
  }
`;
const CardGrid = styled.div `
  display: grid;
  gap: ${fluid(10, 40)} ${fluid(16, 32)};
  grid-template-columns: repeat(2, 1fr);

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(3, 1fr);
    grid-column: 1 / -1;
  }

  @media ${from(Device.DesktopLarge)} {
    grid-template-columns: repeat(6, 1fr);
  }

  [data-site='ess'] & {
    ${ProductCardStyles.PriceWrapper} {
      display: none;
    }

    ${ProductCardStyles.ActionWrapper} {
      margin: 0 auto;
    }

    ${ArticleCardStyles.Container},
    ${ImageCtaCardStyles.Container} {
      --colorTitle: ${brand.global.white};
      --colourText: ${brand.global.white};
    }

    ${TechnicalDocumentCardStyles.Container} {
      --cornerColour: var(--backgroundColour);
    }
  }

  &[data-active='documents'] {
    grid-template-columns: repeat(1, 1fr);

    @media ${from(Device.TabletSmall)} {
      grid-template-columns: repeat(2, 1fr);
    }

    @media ${from(Device.Desktop)} {
      grid-template-columns: repeat(3, 1fr);
    }
  }
`;
const NoResultsText = styled.p `
  ${paragraphLarge};

  grid-column: 1 / -1;
  text-align: center;
  margin: 0;
`;
const SearchTypeaheadStyles = {
    Container,
    Inner,
    CloseButton,
    TabsWrapper,
    CtaWrapper,
    CardGrid,
    NoResultsText,
};
export default SearchTypeaheadStyles;
